import type { IndicatorConfig } from '../components/charts/chartUtils';

export const chartIndicatorSettingIds = ['vwap', 'sma20', 'sma50', 'sma200', 'bb', 'macd'] as const;

export type ChartIndicatorSettingId = (typeof chartIndicatorSettingIds)[number];
export type OverlayChartIndicatorSettingId = Exclude<ChartIndicatorSettingId, 'macd'>;
export type OverlayChartIndicatorConfig = IndicatorConfig & { id: OverlayChartIndicatorSettingId };

export type ChartIndicatorOption = {
  id: ChartIndicatorSettingId;
  label: string;
};

export const chartIndicatorOptions: readonly ChartIndicatorOption[] = [
  { id: 'vwap', label: 'VWAP' },
  { id: 'sma20', label: 'SMA 20' },
  { id: 'sma50', label: 'SMA 50' },
  { id: 'sma200', label: 'SMA 200' },
  { id: 'bb', label: 'Bollinger Bands (20, 2)' },
  { id: 'macd', label: 'MACD' },
];

export const defaultChartIndicatorIds: ChartIndicatorSettingId[] = ['vwap'];

const overlayChartIndicatorConfigs: readonly Omit<OverlayChartIndicatorConfig, 'visible'>[] = [
  { id: 'sma20', type: 'SMA', period: 20, color: '#FF6D00', label: 'SMA 20' },
  { id: 'sma50', type: 'SMA', period: 50, color: '#2962FF', label: 'SMA 50' },
  { id: 'sma200', type: 'SMA', period: 200, color: '#D50000', label: 'SMA 200' },
  { id: 'vwap', type: 'VWAP', color: '#E91E63', label: 'VWAP' },
  { id: 'bb', type: 'BB', period: 20, color: '#00BCD4', label: 'Bollinger Bands (20, 2)' },
];

const chartIndicatorSettingIdSet = new Set<string>(chartIndicatorSettingIds);

export function isChartIndicatorSettingId(value: unknown): value is ChartIndicatorSettingId {
  return typeof value === 'string' && chartIndicatorSettingIdSet.has(value);
}

export function normalizeChartIndicatorSettingIds(raw: unknown): ChartIndicatorSettingId[] {
  if (!Array.isArray(raw)) return [];

  const next: ChartIndicatorSettingId[] = [];
  const seen = new Set<ChartIndicatorSettingId>();

  for (const value of raw) {
    if (!isChartIndicatorSettingId(value) || seen.has(value)) continue;
    seen.add(value);
    next.push(value);
  }

  return next;
}

export function toggleChartIndicatorSettingId(
  current: readonly ChartIndicatorSettingId[],
  id: ChartIndicatorSettingId,
): ChartIndicatorSettingId[] {
  const normalized = normalizeChartIndicatorSettingIds(current);
  return normalized.includes(id)
    ? normalized.filter((value) => value !== id)
    : [...normalized, id];
}

export function buildChartOverlayIndicators(
  selectedIds: readonly ChartIndicatorSettingId[],
): OverlayChartIndicatorConfig[] {
  const selected = new Set(selectedIds);
  return overlayChartIndicatorConfigs.map((indicator) => ({
    ...indicator,
    visible: selected.has(indicator.id),
  }));
}

export function getChartIndicatorLabel(id: ChartIndicatorSettingId): string {
  return chartIndicatorOptions.find((option) => option.id === id)?.label ?? id;
}
